import { BCMSRecord, BCMSStateManagerCRUD, Branding, Link, useBCMSContext, useStateManager } from "@bcm-dev/bcms-vite";
import Classes, { Notification, User } from "model";

import { CenterContentContainer, PageContainer, PageContentView } from "components/shared";
import UINotifiHeader from "components/shared/notifi/UINotifiHeader";
import UINotifiSearch from "components/shared/notifi/UINotifiSearch";
import { SVGAsset } from "res";
import NotificationAlertSectionContainer from "./components/NotificationAlertSectionContainer";

import "./styles.css";
import "./mobile.css";


export default function NotificationsPage(props) {
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultPageData(globalController), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
    const insertIntoArray = (array, value) => array.map(item => item.id === value.id ? value : item, []);

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onSearch: value => {
            BCMSStateManagerCRUD.init(state)
                .update([ "ui", "search", "value" ], value)
                .submit(setState);
        },

        updateNotifications: notification => {
            const updatedArray = insertIntoArray(state.data.notifications, notification);
            
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "notifications" ], updatedArray)
            .submit(setState);
        },

        updateUI: () => {
          BCMSStateManagerCRUD.init(state)
            .update([ "ui", "key" ], Date.now())
            .submit(setState);
        }
    });

    return (
        <PageContainer className="notifications-page" state={ { data, ui } } controller={ controller }>
            <NotificationsContent />
        </PageContainer>
    )
}

export const getDefaultPageData = controller => {
    const branding = controller.state.branding.options;
    let notifications = Notification.initAll();
    const phones = controller.state.contactInfo.filter( item => item.type === "phone");
    const emails = controller.state.contactInfo.filter( item => item.type === "email");

    notifications = branding.notifi_version_abi22 !== "Notifi"
        ? Notification.Limited(notifications)
        : notifications;

    if(branding.scheduled_alerts_abi22 !== "true") {
        notifications = Notification.FilterOutByType(notifications, "scheduled");
    }

    return { 
        notifications,
        emails,
        phones,
        user: User.init(controller.state.user)
    };
};
export const getDefaultPageUI = () => ({
    search: { value: "" },
    key: Date.now()
});

function NotificationsContent(props) {
    const { data, ui, onSearch } = useStateManager();
    const { user } = data;

    let notifications = Notification.SetActivated(data.notifications);
    const notificationsByType = Notification.ByType(notifications);

    const phone = user.phone.value ?? "[Phone]";
    const email = user.email.value ?? "[Email]";
    const phoneLabel = `${ phone } ${ data.phones.length > 1 ? `(+)` : ""}`;
    const emailLabel = `${ email } ${ data.emails.length > 1 ? `(+)` : ""}`;

    let categoryCount = -1;
    const notificationList = Object.keys(notificationsByType).map(notification => {
        const sortedNotifications = Notification.ByCategory(notificationsByType[notification]);
        const search = ui.search.value;

        const filteredNotifications = Object.keys(sortedNotifications)
            .reduce((categories, category) => {
                const alerts = sortedNotifications[category].alerts.filter(alert => {
                    return search !== "" ? alert.name.toLowerCase().indexOf(search.toLowerCase()) !== -1 : true;
                })

                if(alerts.length > 0) {
                    categories.push(
                        <NotificationAlertSectionContainer
                            key={ `Alert_Section_${ category }` }
                            alerts={ alerts }
                            category={ sortedNotifications[category].category } />
                    );
                }

                return categories;
            }, []);

        if(filteredNotifications.length > 0) { categoryCount++; }

        return (
            <div key={ `Category_Header_${ notification }`}>
                {   filteredNotifications.length > 0 &&
                    <p className="section-header flex-width">
                        <span data-point={ "category_header_" + categoryCount }>{ notification }</span>
                    </p>
                }
                { filteredNotifications }
            </div>
        );
    }, []);

    return (
        <PageContentView>
            <div className="notifications-page-container">
                <CenterContentContainer>
                    <UINotifiHeader headerText="Notifications" className="notifications-header">
                        <div className="flex-container flex-horizontal right-header-info-container">
                            <div className="flex-container flex-vertical header-right-column flex-width">
                                <Branding id="notifi_version_abi22" value="Notifi">
                                    <Link to="/notificationsSettings" className="flex-container flex-horizontal align-center flex-height">
                                        <SVGAsset id="MobilePhoneIcon" className="inline-text-icon left-icon" />
                                        <span className="sub-text" data-point="user_phone_type">iPhone</span>
                                    </Link>

                                    <Link to="/notificationsSettings" className="flex-container flex-horizontal align-center flex-height">
                                        <SVGAsset id="SquareMessageBubbleIcon" className="inline-text-icon left-icon" />
                                        <span className="sub-text" data-point="user_phone_number">{ phoneLabel }</span>
                                    </Link>
                                </Branding>
                                
                                <Link to="/notificationsSettings" className="flex-container flex-horizontal align-center flex-height">
                                    <SVGAsset id="EnvelopeIcon" className="inline-text-icon left-icon" />
                                    <span className="sub-text" data-point="user_email_address">{ emailLabel }</span>
                                </Link>
                            </div>
                            <div className="flex-container flex-vertical header-right-column flex-width">
                                <Link to="/notificationsSettings" className="flex-container flex-horizontal align-center flex-height">
                                    <SVGAsset id="GearIcon" className="inline-text-icon left-icon" />
                                    <span className="sub-text" data-point="notification_settings_link">Notifications Settings</span>
                                </Link>
                                
                                <Link to="/notificationsHistory" className="flex-container flex-horizontal align-center flex-height">
                                    <SVGAsset id="ClockArrowIcon" className="inline-text-icon left-icon" />
                                    <span className="sub-text" data-point="notification_history_link">Notifications History</span>
                                </Link>
                            </div>
                        </div>
                    </UINotifiHeader>
                    
                    <UINotifiSearch placeHolder="Search" onChange={onSearch} dataPoint="notifi_search_input" />
                    
                    {notificationList}
                </CenterContentContainer>
            </div>
        </PageContentView>
    );
}